import React, { useState, useEffect, useCallback, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FaTrash, FaCheck, FaSpinner, FaUser } from 'react-icons/fa';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AuthContext } from "../context/AuthContext";

function CommentsAnnotations({ quill, annotations: propAnnotations, onAnnotationsUpdate, documentId }) {
  const [annotations, setAnnotations] = useState(propAnnotations || []);
  const [newAnnotation, setNewAnnotation] = useState('');
  const [activeAnnotation, setActiveAnnotation] = useState(null);
  const [loading, setLoading] = useState(false);
  const { authState } = useContext(AuthContext);

  useEffect(() => {
    setAnnotations(propAnnotations || []);
  }, [propAnnotations]);

  const addAnnotationBlot = useCallback(() => {
    if (!quill) return;
    const range = quill.getSelection();
    if (range && range.length > 0) {
      const id = uuidv4();
      const text = quill.getText(range.index, range.length);
      quill.formatText(range.index, range.length, { annotation: id, background: 'yellow' });
      const newAnnotations = [...annotations, { 
        id, 
        text: '', 
        range, 
        commentedText: text,
        userId: {
          _id: authState.user._id,
          firstname: authState.user.firstname,
          lastname: authState.user.lastname
        }
      }];
      setAnnotations(newAnnotations);
      onAnnotationsUpdate(newAnnotations);
      setActiveAnnotation(id);
    } else {
      alert("Please select some text before adding an annotation.");
    }
  }, [quill, annotations, onAnnotationsUpdate, authState.user]);

  useEffect(() => {
    if (!quill) return;

    quill.keyboard.addBinding({ key: 'M', altKey: true }, addAnnotationBlot);

    const toolbar = quill.getModule('toolbar');
    if (toolbar) {
      toolbar.addHandler('annotation', addAnnotationBlot);
    }

    const handleTextChange = (delta, oldDelta, source) => {
      if (source !== 'user') return;

      const updatedAnnotations = annotations.map(annotation => {
        const annotationRange = quill.getFormat(annotation.range.index, annotation.range.length);
        if (annotationRange.annotation === annotation.id) {
          const updatedText = quill.getText(annotation.range.index, annotation.range.length);
          return { ...annotation, commentedText: updatedText };
        }
        return annotation;
      });

      setAnnotations(updatedAnnotations);
      onAnnotationsUpdate(updatedAnnotations);
    };

    quill.on('text-change', handleTextChange);

    return () => {
      quill.off('text-change', handleTextChange);
      const bindings = quill.keyboard.bindings;
      if (bindings && bindings['M']) {
        bindings['M'] = bindings['M'].filter(binding => 
          !(binding.altKey && binding.key === 'M')
        );
      }
      if (toolbar) {
        toolbar.addHandler('annotation', null);
      }
    };
  }, [quill, annotations, onAnnotationsUpdate, addAnnotationBlot]);

  const handleAnnotationSubmit = async (id) => {
    setLoading(true);
    const updatedAnnotation = annotations.find(annotation => annotation.id === id);
    const annotationUpdate = {
      id: updatedAnnotation.id,
      text: newAnnotation,
      range: updatedAnnotation.range,
      commentedText: updatedAnnotation.commentedText
    };
  
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${documentId}/annotations`, {
        annotations: [annotationUpdate]
      }, {
        headers: { 'x-auth-token': authState.token }
      });
  
      const updatedAnnotations = annotations.map(annotation =>
        annotation.id === id ? {...annotation, text: newAnnotation} : annotation
      );
  
      setAnnotations(updatedAnnotations);
      onAnnotationsUpdate(updatedAnnotations);
      setNewAnnotation('');
      setActiveAnnotation(null);
    } catch (error) {
      console.error('Error submitting annotation:', error);
      alert('Failed to save annotation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnnotationDelete = async (id) => {
    setLoading(true);
    try {
      // Call the DELETE endpoint
      const response = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${documentId}/annotations/${id}`, {
        headers: { 'x-auth-token': authState.token }
      });
  
      // Update local state if the delete was successful
      const updatedAnnotations = annotations.filter(annotation => annotation.id !== id);
      setAnnotations(updatedAnnotations);
      onAnnotationsUpdate(updatedAnnotations);
  
      // Remove the annotation formatting from the quill editor
      if (quill) {
        const annotationBlot = quill.getFormat().annotation;
        if (annotationBlot === id) {
          quill.formatText(0, quill.getLength(), { annotation: false, background: false });
        }
      }
  
      // Show success message from the server response
      toast.success(response.data.message || 'Annotation deleted successfully.');
    } catch (error) {
      console.error('Error deleting annotation:', error);
      if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message || 'Annotation not found. It may have been already deleted.');
      } else {
        toast.error(error.response.data.message || 'Failed to delete annotation. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const highlightAnnotation = (annotation) => {
    if (quill) {
      quill.setSelection(annotation.range.index, annotation.range.length);
      quill.formatText(annotation.range.index, annotation.range.length, { background: 'yellow' });
      quill.scrollIntoView();
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md shadow-sm">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Annotations</h3>
        <button
          onClick={addAnnotationBlot}
          className="text-xs text-blue-600 hover:text-blue-800"
          disabled={loading}
        >
          Add annotation (Alt+M)
        </button>
      </div>
      <div className="p-2 max-h-64 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center my-2">
            <FaSpinner className="animate-spin text-indigo-500" />
          </div>
        ) : annotations.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No annotations yet</p>
        ) : (
          annotations.map(annotation => (
            <div key={annotation.id} className="mb-2 p-2 bg-gray-50 rounded-md text-sm">
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-center">
                  <FaUser className="text-gray-400 mr-2" />
                  <span className="text-gray-600 font-medium">
                    {annotation.userId.firstname} {annotation.userId.lastname}
                  </span>
                </div>
                <button
                  onClick={() => handleAnnotationDelete(annotation.id)}
                  className="ml-2 text-red-500 hover:text-red-700"
                  disabled={loading}
                >
                  <FaTrash />
                </button>
              </div>
              <p className="text-gray-600 italic mb-1">"{annotation.commentedText}"</p>
              {annotation.id === activeAnnotation ? (
                <>
                  <textarea
                    value={newAnnotation}
                    onChange={(e) => setNewAnnotation(e.target.value)}
                    className="w-full p-1 border rounded text-sm mt-1"
                    placeholder="Enter your annotation"
                    rows="2"
                  />
                  <div className="flex justify-end mt-1">
                    <button
                      onClick={() => handleAnnotationSubmit(annotation.id)}
                      className="px-2 py-1 bg-blue-500 text-white rounded-md text-xs flex items-center"
                      disabled={loading}
                    >
                      <FaCheck className="mr-1" /> Submit
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-700">{annotation.text || 'No annotation text'}</p>
                  <button
                    onClick={() => highlightAnnotation(annotation)}
                    className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                    disabled={loading}
                  >
                    Go to text
                  </button>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default CommentsAnnotations;